import { showGuestProjectWithImageOnly } from '@/core/constants/appConfig';
import { useTranslation } from '@/core/hooks';
import {
  trackGuestProjectBrochureDownloadClick,
  trackGuestProjectMasterPlanViewClick,
} from '@/core/services/trackDynatraceCatalog';
import { resolveDocumentDownloadUrls } from '@/core/utils/brochureDownload';
import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  filterGuestProjectsForListing,
  mapProjectToCard,
  mapProjectToDetails,
  sortProjectsByContentfulOrder,
} from '../services/mapGuestProjects';
import { openGuestDocument } from '../services/openGuestDocument';
import type {
  GuestListLoadState,
  GuestPropertyCardModel,
  GuestUnitDetailsModel,
} from '../types/guest.types';
import { useGuestLabels } from './useGuestLabels';
import { useGuestProjectAssets } from './useGuestProjectImages';
import { useGuestProjectsQuery } from './useGuestProjectsQuery';

export interface UseGuestUnitDetailsResult {
  readonly loadState: GuestListLoadState;
  readonly unit: GuestUnitDetailsModel | null;
  readonly exploreProperties: ReadonlyArray<GuestPropertyCardModel>;
  readonly canLoadMore: boolean;
  readonly isFetchingNextPage: boolean;
  readonly isPrefetchingCatalogue: boolean;
  readonly loadMore: () => void;
  readonly handleDocumentPress: (documentId: string) => void;
  readonly downloadingDocumentId: string | null;
}

/**
 * Guest project details — /v1/projects + Contentful images/brochure by project_id.
 * Catalogue pages prefetch in the background until the deep-linked project is found.
 */
export function useGuestUnitDetails(): UseGuestUnitDetailsResult {
  const labels = useGuestLabels();
  const { currentLanguage } = useTranslation();
  const locale = currentLanguage === 'ar' ? 'ar' : 'en';
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const id = useMemo(() => {
    const raw = Array.isArray(params.id) ? params.id[0] : params.id;
    return raw?.trim() || '';
  }, [params.id]);

  const {
    projects,
    isLoading,
    isError,
    canLoadMore,
    loadMore,
    isFetchingNextPage,
    isPrefetchingCatalogue,
  } = useGuestProjectsQuery();
  const {
    imagesByProjectId,
    brochureUrlsByProjectId,
    nameByProjectId,
    descriptionByProjectId,
    orderByProjectId,
    isContentfulPending,
  } = useGuestProjectAssets();

  const match = useMemo(() => {
    if (!id) {
      return null;
    }
    return projects.find((project) => project.project_id === id) ?? null;
  }, [id, projects]);

  const unit = useMemo(() => {
    if (!match) {
      return null;
    }
    const brochureUrls = brochureUrlsByProjectId[match.project_id];
    return mapProjectToDetails(
      match,
      imagesByProjectId[match.project_id] ?? [],
      {
        ...(brochureUrls?.length
          ? { brochureUrls, brochureTitle: labels.brochure }
          : {}),
        contentfulName: nameByProjectId[match.project_id],
        contentfulDescription: descriptionByProjectId[match.project_id],
        locale,
      },
    );
  }, [
    match,
    imagesByProjectId,
    brochureUrlsByProjectId,
    nameByProjectId,
    descriptionByProjectId,
    labels.brochure,
    locale,
  ]);

  const exploreProperties = useMemo(() => {
    if (!id) {
      return [];
    }
    const others = projects.filter((project) => project.project_id !== id);
    const sorted = sortProjectsByContentfulOrder(others, orderByProjectId);
    const visible = filterGuestProjectsForListing(
      sorted,
      imagesByProjectId,
      showGuestProjectWithImageOnly,
    );
    return visible.map((project) =>
      mapProjectToCard(
        project,
        labels.unitsBadge,
        imagesByProjectId[project.project_id] ?? [],
        {
          contentfulName: nameByProjectId[project.project_id],
          locale,
        },
      ),
    );
  }, [
    id,
    projects,
    labels.unitsBadge,
    imagesByProjectId,
    nameByProjectId,
    orderByProjectId,
    locale,
  ]);

  useEffect(() => {
    if (!id) {
      router.replace('/guest');
    }
  }, [id]);

  const handleDocumentPress = useCallback(
    (documentId: string) => {
      const doc = unit?.documents?.find((item) => item.id === documentId);
      if (!doc || downloadingDocumentId) {
        return;
      }
      const urls = resolveDocumentDownloadUrls(doc);
      if (urls.length === 0 || !unit?.title) {
        return;
      }
      if (doc.action === 'view') {
        trackGuestProjectMasterPlanViewClick(unit.id);
      } else {
        trackGuestProjectBrochureDownloadClick(unit.id);
      }
      setDownloadingDocumentId(documentId);
      void openGuestDocument(urls, unit.title)
        .catch(() => undefined)
        .finally(() => {
          setDownloadingDocumentId(null);
        });
    },
    [unit, downloadingDocumentId],
  );

  const stillPagingForMatch =
    Boolean(id) &&
    !match &&
    !isError &&
    (isLoading || isPrefetchingCatalogue);

  let loadState: GuestListLoadState = 'ready';
  if (!id || stillPagingForMatch) {
    loadState = 'loading';
  } else if (isError) {
    loadState = 'error';
  } else if (!unit) {
    loadState = 'empty';
  }

  return {
    loadState,
    unit,
    exploreProperties,
    canLoadMore,
    isFetchingNextPage,
    isPrefetchingCatalogue:
      isPrefetchingCatalogue ||
      (showGuestProjectWithImageOnly && isContentfulPending),
    loadMore,
    handleDocumentPress,
    downloadingDocumentId,
  };
}
